type LogLevel = "debug" | "info" | "warn" | "error";

// Path segments that look like business identifiers — claim, policy, agency,
// agent numbers — get replaced with "*" before logging so production logs
// don't carry restricted-claim numbers or other PII into search/audit
// systems. UUIDs (claim_current_attribute.id, etc.) get the same treatment.
// The digit-after-hyphen anchor on the first alternative keeps real auth
// path segments like "sign-in" from being masked.
const ID_SEGMENT =
  /^([A-Z]{2,4}-[0-9][A-Z0-9]*|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export function maskPath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => (ID_SEGMENT.test(seg) ? "*" : seg))
    .join("/");
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function getMinLevel(): LogLevel {
  if (typeof process === "undefined" || !process.env) return "debug";
  const explicit = process.env.LOG_LEVEL as LogLevel | undefined;
  if (explicit) return explicit;
  return process.env.NODE_ENV === "production" ? "info" : "debug";
}

const minLevel: LogLevel = getMinLevel();

function normalize(value: unknown): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (Array.isArray(value)) return value.map(normalize);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = normalize(v);
    }
    return out;
  }
  return value;
}

function emit(level: LogLevel, message: string, data?: Record<string, unknown>) {
  if (LOG_LEVELS[level] < LOG_LEVELS[minLevel]) return;

  const entry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...(data ? (normalize(data) as Record<string, unknown>) : {}),
  };

  const line = JSON.stringify(entry);

  if (level === "error") {
    console.error(line);
  } else if (level === "warn") {
    console.warn(line);
  } else {
    console.log(line);
  }
}

export const logger = {
  debug: (msg: string, data?: Record<string, unknown>) => emit("debug", msg, data),
  info: (msg: string, data?: Record<string, unknown>) => emit("info", msg, data),
  warn: (msg: string, data?: Record<string, unknown>) => emit("warn", msg, data),
  error: (msg: string, data?: Record<string, unknown>) => emit("error", msg, data),

  /** Log an HTTP request with duration */
  request(req: Request, status: number, durationMs: number, extra?: Record<string, unknown>) {
    const url = new URL(req.url);
    emit(status >= 500 ? "error" : status >= 400 ? "warn" : "info", "http_request", {
      method: req.method,
      path: maskPath(url.pathname),
      status,
      durationMs: Math.round(durationMs),
      userAgent: req.headers.get("user-agent") ?? undefined,
      ip: req.headers.get("x-forwarded-for") ?? undefined,
      ...extra,
    });
  },
};
